import { useLoadScript } from "@react-google-maps/api";
import { useEffect, useMemo, useState } from "react";
import usePlacesAutocomplete, { Suggestion } from "use-places-autocomplete";

// import { AddressSuggestion } from "@/components/GoogleAddressAutocomplete/types";
import { AddressSuggestion } from "./types";
import { GOOGLE_MAPS_LIBRARIES_TO_USE } from "../../constants/google-maps";

// import {
//   getGoogleFormattedAddress,
//   getGooglePostalCode,
// } from "@/ui/utils/google/googleMaps";


import { getGoogleFormattedAddress,getGooglePostalCode } from "../../../utils/google/googleMaps";
interface UseGoogleSuggestionsOptions {
  defaultValue?: string;
  getShortAddress?: boolean;
}

export function useGoogleSuggestions({
  defaultValue,
  getShortAddress = false,
}: UseGoogleSuggestionsOptions) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>();
  const [isFormatting, setIsFormatting] = useState(false);
  const [formattingError, setFormattingError] = useState<any>(null);
  const { isLoaded: isGoogleMapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: GOOGLE_MAPS_LIBRARIES_TO_USE,
  });
  const {
    ready: isPlacesAutocompleteReady,
    suggestions: { status: googleSuggestionsStatus, data: rawData, loading }, // results from Google Places API for the given search term
    setValue: setAutoCompleteValue,
    init: initPlacesAutocomplete,
    clearSuggestions,
  } = usePlacesAutocomplete({
    defaultValue,
    requestOptions: {
      componentRestrictions: { country: "us" }, // restrict search to US
      types: ["address"],
    },
    debounce: 300,
    cache: 86400,
  });

  const error = useMemo(
    () =>
      googleSuggestionsStatus !== "OK" ? googleSuggestionsStatus : undefined,
    [googleSuggestionsStatus],
  );

  useEffect(() => {
    if (isGoogleMapsLoaded && !isPlacesAutocompleteReady) {
      initPlacesAutocomplete();
    }
  }, [isGoogleMapsLoaded, isPlacesAutocompleteReady, initPlacesAutocomplete]);

  useEffect(() => {
    setIsFormatting(true);
    setFormattingError(null);

    getFormattedAddress(rawData, getShortAddress)
      .then(suggestions => setSuggestions(suggestions))
      .catch(err => {
        setFormattingError(err);
        setSuggestions([]);
      })
      .finally(() => setIsFormatting(false));
  }, [rawData, getShortAddress]);

  return {
    data: suggestions,
    isLoading: loading || isFormatting,
    error: error ?? formattingError,
    isPlacesAutocompleteReady,
    setAutoCompleteValue,
    clearSuggestions,
  };
}

const getFormattedAddress = async (
  suggestions: Suggestion[],
  getShortAddress: boolean = false,
): Promise<AddressSuggestion[]> => {
  let formattedData: AddressSuggestion[] = await Promise.all(
    suggestions.map(async (suggestion: Suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
      } = suggestion;

      const googlePostalCode = getGooglePostalCode(secondary_text);

      const formattedAddress = await getGoogleFormattedAddress(
        { placeId: place_id },
        getShortAddress,
      );
      return {
        ...formattedAddress,
        mainText: main_text,
        placeId: place_id,
        googlePostalCode,
      };
    }),
  );

  return formattedData;
};
